// Account Statements — per-company aggregates. Click a row to see the
// company's orders + a paid/outstanding split.
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@workos-inc/authkit-react'
import { apiFetch } from '../../lib/api'
import { StatusBadge } from './Overview'

interface Row {
  id: string; name: string; billingEmail: string | null; paymentTerms: string | null
  isActive: boolean
  outstandingCents: number; paidCents: number
  orderCount: number; lastOrderAt: string | null
}
interface Detail {
  company: { id: string; name: string; billingEmail: string | null; paymentTerms: string | null }
  orders: Array<{ id: string; number: number; status: string; totalCents: number; createdAt: string; paidAt: string | null; shippingName: string | null }>
  totals: { outstandingCents: number; paidCents: number }
}

export default function Statements() {
  const { getAccessToken } = useAuth()
  const [rows, setRows] = useState<Row[]>([])
  const [err, setErr] = useState<string | null>(null)
  const [openId, setOpenId] = useState<string | null>(null)
  const [detail, setDetail] = useState<Detail | null>(null)

  useEffect(() => {
    (async () => {
      try {
        const d = await apiFetch<{ items: Row[] }>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/admin/statements')
        setRows(d.items); setErr(null)
      } catch (e) { setErr((e as Error).message) }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!openId) { setDetail(null); return }
    (async () => {
      try {
        const d = await apiFetch<Detail>(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/statements/${openId}`)
        setDetail(d)
      } catch (e) { setErr((e as Error).message) }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId])

  return (
    <div className="p-8">
      <div className="mb-6">
        <div className="eyebrow">Customers</div>
        <h1 className="font-heading uppercase font-bold text-3xl text-ink">Account Statements</h1>
      </div>

      {err && <div className="card text-red-700 mb-4">{err}</div>}

      <div className="card !p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface text-[10px] uppercase tracking-wider text-ink-3">
            <tr>
              <th className="text-left px-4 py-2 font-bold">Company</th>
              <th className="text-left px-4 py-2 font-bold">Billing email</th>
              <th className="text-left px-4 py-2 font-bold">Terms</th>
              <th className="text-right px-4 py-2 font-bold">Orders</th>
              <th className="text-right px-4 py-2 font-bold">Outstanding</th>
              <th className="text-right px-4 py-2 font-bold">Paid</th>
              <th className="text-right px-4 py-2 font-bold">Last order</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-ink-3 text-sm">No companies set up yet. Add one in the Companies section.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-surface transition-colors cursor-pointer" onClick={() => setOpenId(r.id)}>
                <td className="px-4 py-3 text-ink font-semibold">{r.name}</td>
                <td className="px-4 py-3 text-ink-2">{r.billingEmail ?? '—'}</td>
                <td className="px-4 py-3 text-ink-2">{r.paymentTerms ?? '—'}</td>
                <td className="px-4 py-3 text-right text-ink-2">{r.orderCount}</td>
                <td className={'px-4 py-3 text-right font-semibold ' + (r.outstandingCents > 0 ? 'text-red-700' : 'text-ink')}>${(r.outstandingCents / 100).toFixed(2)}</td>
                <td className="px-4 py-3 text-right text-ink font-semibold">${(r.paidCents / 100).toFixed(2)}</td>
                <td className="px-4 py-3 text-right text-xs text-ink-3">{r.lastOrderAt ? new Date(r.lastOrderAt).toLocaleDateString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {openId && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-baseline justify-between mb-4">
              <div>
                <div className="eyebrow">Statement</div>
                <h3 className="font-heading uppercase font-bold text-xl text-ink">{detail?.company.name ?? 'Loading…'}</h3>
                <div className="text-xs text-ink-3">{detail?.company.billingEmail} · {detail?.company.paymentTerms ?? 'No terms set'}</div>
              </div>
              <button onClick={() => setOpenId(null)} className="text-ink-3 text-2xl">×</button>
            </div>
            {detail && (
              <>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="card"><div className="text-[10px] uppercase tracking-[0.16em] text-ink-3">Outstanding</div><div className="font-heading font-bold text-2xl text-red-700 mt-1">${(detail.totals.outstandingCents / 100).toFixed(2)}</div></div>
                  <div className="card"><div className="text-[10px] uppercase tracking-[0.16em] text-ink-3">Paid (lifetime)</div><div className="font-heading font-bold text-2xl text-ink mt-1">${(detail.totals.paidCents / 100).toFixed(2)}</div></div>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-surface text-[10px] uppercase tracking-wider text-ink-3">
                    <tr><th className="text-left px-3 py-2">Order</th><th className="text-left px-3 py-2">Status</th><th className="text-right px-3 py-2">Total</th><th className="text-right px-3 py-2">Date</th></tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {detail.orders.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-ink-3">No orders for this company.</td></tr>}
                    {detail.orders.map((o) => (
                      <tr key={o.id} className="hover:bg-surface transition-colors">
                        <td className="px-3 py-2 font-mono text-xs">
                          <Link to={`/admin/orders/${o.id}`} className="text-gold-dark hover:underline">#{o.number}</Link>
                        </td>
                        <td className="px-3 py-2"><StatusBadge status={o.status} /></td>
                        <td className="px-3 py-2 text-right font-semibold text-ink">${(o.totalCents / 100).toFixed(2)}</td>
                        <td className="px-3 py-2 text-right text-xs text-ink-3">{new Date(o.createdAt).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
